import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const EASE = [0.16, 1, 0.3, 1];

const H1_LINES = [
  ["Experience", "is", "an", "unfair", "advantage."],
  ["We", "compound", "it."],
];

const wordVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: EASE, delay: 0.5 + i * 0.06 },
  }),
};

let wordIndex = 0;

export default function Hero() {
  wordIndex = 0;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-noir text-cream"
    >
      {/* Full-bleed cinematic entrepreneur video */}
      <motion.div
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.8, ease: EASE }}
        className="absolute inset-0 z-0"
      >
        <video
          data-testid="hero-video"
          className="h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/hero-poster.jpg"
        >
          <source src="/hero.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* scrims tuned for BRIGHT footage: even tint + centered vignette for centered text */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-noir/50" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-noir via-noir/50 to-noir/40" />
      <div className="pointer-events-none absolute inset-0 z-[1] [background:radial-gradient(52%_46%_at_50%_50%,rgba(8,8,8,0.9)_0%,rgba(8,8,8,0.5)_52%,transparent_82%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-40 bg-gradient-to-b from-noir/70 to-transparent" />

      {/* content, centered */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col items-center justify-center px-6 text-center">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.25 }}
          className="mb-6 text-[0.66rem] font-semibold uppercase tracking-[0.34em] text-gold"
        >
          Govil Ventures
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="mx-auto max-w-5xl font-serif font-bold leading-[1.02] tracking-tight text-cream"
          style={{ fontSize: "clamp(2.1rem, 4.6vw, 3.9rem)", fontWeight: 700 }}
        >
          {H1_LINES.map((line, li) => (
            <span key={li} className="block overflow-hidden py-[0.02em]">
              <span className="inline-block">
                {line.map((word) => {
                  const i = wordIndex++;
                  return (
                    <span key={i} className="mr-[0.22em] inline-block overflow-hidden align-bottom">
                      <motion.span
                        className="inline-block"
                        custom={i}
                        variants={wordVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        {word}
                      </motion.span>
                    </span>
                  );
                })}
              </span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.25 }}
          className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-cream/85 md:text-[1rem]"
        >
          We partner with experienced operators to turn a lifetime of insight
          into the most important company they&rsquo;ll ever build.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.45 }}
          className="mt-9 flex flex-wrap items-center justify-center gap-3.5"
        >
          <a
            data-testid="hero-secondary"
            href="#portfolio"
            className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-2.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            View Portfolio
          </a>
          <Link
            data-testid="hero-operators"
            to="/operators"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-2.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-noir transition-all duration-300 hover:bg-gold/90"
          >
            Meet the Operators
          </Link>
        </motion.div>
      </div>

      <motion.div
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 md:flex"
      >
        <span className="text-[10px] font-medium uppercase tracking-[0.3em] text-white/40">Scroll</span>
        <span className="h-14 w-px overflow-hidden bg-white/10">
          <span className="block h-full w-full origin-top animate-scroll-line bg-gold" />
        </span>
      </motion.div>
    </section>
  );
}
